import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useJsonLd } from '../../hooks/useJsonLd';

const navLinks = [
  { label: '事業内容', href: '/#business' },
  { label: 'サービス', href: '/#services' },
  { label: 'お問い合わせ', href: '/#contact' },
];

function CompanyNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-stone-200' : 'bg-white/95 backdrop-blur-sm border-b border-stone-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
        <Link to="/">
          <img
            src="https://static.readdy.ai/image/13912524abd39dd1dc7f5a01ff0217d1/7addc6331c966f3e395b0cb5315fdc96.jpeg"
            alt="株式会社グランドビレッジ"
            className="h-10 lg:h-12 w-auto object-contain"
          />
        </Link>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="whitespace-nowrap text-sm font-medium text-stone-700 hover:opacity-70 transition-all duration-300 cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <span className="whitespace-nowrap text-sm font-medium text-amber-800 border-b border-amber-700 pb-0.5 cursor-default">
            会社概要
          </span>
        </nav>
      </div>
    </header>
  );
}

function CompanyFooter() {
  return (
    <footer className="w-full bg-amber-950 py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-10 justify-between items-start">
          <div className="flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/13912524abd39dd1dc7f5a01ff0217d1/7addc6331c966f3e395b0cb5315fdc96.jpeg"
              alt="株式会社グランドビレッジ"
              className="h-10 w-auto object-contain mb-3"
            />
            <p className="text-amber-200/80 text-sm font-medium mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              株式会社グランドビレッジ
            </p>
            <p className="text-amber-200/60 text-xs leading-relaxed max-w-xs">
              貸別荘・民泊の企画から申請、管理、<br />
              リフォームまでトータルサポート
            </p>
          </div>
          <nav className="flex flex-wrap gap-x-8 gap-y-3">
            <Link to="/#business" className="text-amber-200/60 text-xs hover:text-amber-300 transition-colors duration-300 cursor-pointer whitespace-nowrap">事業内容</Link>
            <Link to="/#contact" className="text-amber-200/60 text-xs hover:text-amber-300 transition-colors duration-300 cursor-pointer whitespace-nowrap">お問い合わせ</Link>
            <Link to="/company" className="text-amber-200/80 text-xs hover:text-amber-300 transition-colors duration-300 cursor-pointer whitespace-nowrap">会社概要</Link>
          </nav>
        </div>
        <div className="mt-10 pt-8 border-t border-amber-900 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-amber-200/40 text-xs">
            &copy; {new Date().getFullYear()} 株式会社グランドビレッジ. All Rights Reserved.
          </p>
          <p className="text-amber-200/40 text-xs">Grand Village Co., Ltd.</p>
        </div>
      </div>
    </footer>
  );
}

export default function CompanyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  useJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/company#webpage`,
        url: `${siteUrl}/company`,
        name: '会社概要 | 株式会社グランドビレッジ',
        isPartOf: { '@id': `${siteUrl}/#website` },
        about: { '@id': `${siteUrl}/#organization` },
        description: '株式会社グランドビレッジの会社概要。東京都世田谷区・静岡県伊東市を拠点に、貸別荘の運営・民泊申請代行・管理・リフォームをトータルサポート。代表取締役：太田嶋信二。',
        inLanguage: 'ja',
        dateModified: '2026-03-25',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: '会社概要', item: `${siteUrl}/company` },
          ],
        },
      },
      {
        '@type': 'AboutPage',
        '@id': `${siteUrl}/company#aboutpage`,
        url: `${siteUrl}/company`,
        name: '会社概要 | 株式会社グランドビレッジ',
        description: '株式会社グランドビレッジの会社情報・経営理念・代表メッセージ・運営物件のご紹介。',
        inLanguage: 'ja',
        about: {
          '@type': ['Organization', 'LocalBusiness'],
          '@id': `${siteUrl}/#organization`,
          name: '株式会社グランドビレッジ',
          alternateName: 'Grand Village Co., Ltd.',
          url: `${siteUrl}/`,
          email: 'globalvillage4548tvc@gmail.com',
          founder: { '@type': 'Person', name: '太田嶋 信二' },
          address: [
            {
              '@type': 'PostalAddress',
              postalCode: '150-0021',
              streetAddress: '恵比寿西2-16-11',
              addressLocality: '渋谷区',
              addressRegion: '東京都',
              addressCountry: 'JP',
            },
            {
              '@type': 'PostalAddress',
              postalCode: '413-0231',
              streetAddress: '富戸1317-2661',
              addressLocality: '伊東市',
              addressRegion: '静岡県',
              addressCountry: 'JP',
            },
          ],
          bankAccount: {
            '@type': 'BankAccount',
            bankName: '三井住友銀行 トランクNORTH支店',
            accountId: '0382161',
          },
        },
      },
    ],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative font-sans bg-white" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <CompanyNavbar />

      {/* Hero Section */}
      <section className="relative w-full h-64 lg:h-80 overflow-hidden mt-16 lg:mt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/5518b8637baa6c9b4def7b66dc98d56d.jpg"
          alt="会社概要"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50 flex flex-col items-center justify-center">
          <p className="text-amber-200/80 text-xs tracking-[0.3em] mb-3 uppercase">Company Profile</p>
          <h1 className="text-3xl lg:text-5xl font-light text-white tracking-widest" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            会社概要
          </h1>
        </div>
      </section>

      {/* Policy / Message Section */}
      <section className="w-full py-20 lg:py-28 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-xs tracking-[0.3em] text-amber-700 mb-6 uppercase">Our Philosophy</p>
          <h2 className="text-2xl lg:text-3xl font-light text-stone-800 leading-relaxed mb-10" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            「住みたくなる心地よさ」を、<br className="hidden lg:block" />すべての空間に。
          </h2>
          <div className="w-12 h-px bg-amber-700 mx-auto mb-10" />
          <div className="text-stone-600 text-sm lg:text-base leading-loose space-y-6 text-left">
            <p>
              私たちグランドビレッジは、「お客様との信頼関係」を何よりも大切にしています。物件を貸す・借りる、その一歩一歩に、お客様の大切な想いが込められていることを忘れません。だからこそ、誠実に、丁寧に、向き合い続けることを約束します。
            </p>
            <p>
              空間というものは、そこに訪れる人の気持ちを映す鏡だと思っています。整えられた場所、光の入り方、素材のぬくもり——そういった細部への配慮が積み重なって、はじめて「また来たい」と感じてもらえる場所が生まれます。
            </p>
            <p>
              私たちが手がけるのは、単なる宿泊施設の運営ではありません。そこに泊まるすべての人が、日常のざわめきから離れ、ほっと深呼吸できるような、心地いい空間をつくること。住みたくなるような居心地のよさを追求し、何度でも足を運んでもらえる場所を生み出すこと、それが私たちの使命です。
            </p>
            <p>
              貸別荘・民泊の企画から申請、管理、リフォームまで——すべての工程で「本当に喜んでもらえるか」を問いながら、一つひとつ真摯に取り組んでいます。オーナー様の思いを形にし、ゲストの方々に豊かな体験を届けること。その橋渡しを、私たちは誇りをもって担います。
            </p>
          </div>
          {/* Handwritten Signature */}
          <div className="mt-14 flex items-end justify-end gap-4">
            {/* Portrait Photo */}
            <div className="w-14 h-18 flex-shrink-0 overflow-hidden rounded-sm" style={{ width: '56px', height: '72px' }}>
              <img
                src="https://static.readdy.ai/image/13912524abd39dd1dc7f5a01ff0217d1/1e72b5b0581ebfbd18599983cd804cec.jpeg"
                alt="代表取締役 太田嶋信二"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Signature Text */}
            <div className="flex flex-col items-end">
              <p className="text-stone-400 text-xs mb-1.5 tracking-wider">代表取締役</p>
              <p
                style={{
                  fontFamily: "'Yuji Syuku', 'Noto Serif JP', serif",
                  fontSize: '1.25rem',
                  letterSpacing: '0.12em',
                  transform: 'rotate(-1.5deg)',
                  display: 'inline-block',
                  borderBottom: '1px solid #a78040',
                  paddingBottom: '3px',
                  color: '#44403c',
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                太田嶋信二
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview Table */}
      <section className="w-full py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] text-amber-700 mb-4 uppercase">Company Overview</p>
            <h2 className="text-2xl lg:text-3xl font-light text-stone-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              会社情報
            </h2>
            <div className="w-12 h-px bg-amber-700 mx-auto mt-6" />
          </div>

          <div className="divide-y divide-stone-100">
            {[
              { label: '会社名', value: '株式会社グランドビレッジ' },
              { label: '本店所在地', value: '〒150-0021　東京都渋谷区恵比寿西2-16-12' },
              { label: '支店所在地', value: '〒413-0231　静岡県伊東市富戸1317-2661' },
              { label: '代表取締役', value: '太田嶋 信二' },
              { label: 'メールアドレス', value: 'globalvillage4548tvc@gmail.com' },
              { label: '事業内容', value: '貸別荘・民泊の企画・プロデュース／民泊申請代行／簡易宿所申請代行／民泊貸別荘の管理業務／建物リフォーム事業／貸別荘の運営' },
              {
                label: '取引銀行',
                value: '三井住友銀行　トランクNORTH支店\n普通預金　口座番号：0382161\n名義：株式会社グランドビレッジ',
                multiline: true,
              },
            ].map((row) => (
              <div key={row.label} className="flex flex-col sm:flex-row py-5 gap-2 sm:gap-0">
                <dt className="text-stone-500 text-sm font-medium w-full sm:w-44 flex-shrink-0">{row.label}</dt>
                <dd className="text-stone-800 text-sm leading-relaxed flex-1">
                  {row.multiline
                    ? row.value.split('\n').map((line, i) => (
                        <span key={i} className="block">{line}</span>
                      ))
                    : row.value}
                </dd>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Property */}
      <section className="w-full py-20 lg:py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-amber-700 mb-4 uppercase">Our Property</p>
            <h2 className="text-2xl lg:text-3xl font-light text-stone-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              運営する貸別荘
            </h2>
            <div className="w-12 h-px bg-amber-700 mx-auto mt-6" />
          </div>

          <a
            href="https://nels-house.com/"
            target="_blank"
            rel="nofollow noreferrer"
            className="group block bg-white rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer"
          >
            <div className="flex flex-col sm:flex-row">
              <div className="w-full sm:w-64 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20luxurious%20Japanese%20rental%20cottage%20villa%20wooden%20interior%20warm%20amber%20light%20natural%20materials%20bed%20linen%20white%20minimalist%20elegant%20mountain%20view%20window%20nature%20peaceful%20retreat%20serene&width=600&height=400&seq=nels-house-001&orientation=landscape"
                  alt="ネルの家"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex-1 p-8 flex flex-col justify-center">
                <p className="text-xs tracking-widest text-amber-700 mb-2 uppercase">Rental Villa</p>
                <h3 className="text-xl font-medium text-stone-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  ネルの家
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed mb-5">
                  静岡県伊東市に佇む、心ほぐれる一棟貸し別荘。<br />
                  自然のなかで「住む」ような特別な滞在を。
                </p>
                <span className="inline-flex items-center gap-2 text-amber-800 text-sm font-medium group-hover:gap-3 transition-all duration-300 whitespace-nowrap">
                  公式サイトを見る
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Back to Home */}
      <section className="w-full py-16 bg-white text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-stone-600 text-sm hover:text-amber-800 transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          トップページへ戻る
        </Link>
      </section>

      <CompanyFooter />
    </div>
  );
}
